import dayjs, { type Dayjs } from 'dayjs'
import type { Dispatch, SetStateAction } from 'react'

import { NEW_PATIENT_FORM_KEYS } from 'features/Home/constants/infoNewPatientFormKeys'
import type { IPatientRoom } from 'features/Home/interfaces/IInfoPatient'

import type { SearchCriteria } from './ehrAppointmentV2.types'
import { roomForDay } from './futureRoom'

type Patch = { time?: Date; duration?: number; room?: string }

type Args = {
  values: { time?: Date | string; room?: string | number }
  timeZone?: string
  rooms: IPatientRoom[]
  setFieldValue: (field: string, value: unknown) => void
  setCriteria: Dispatch<SetStateAction<SearchCriteria>>
  refreshGuards: (patch: Patch) => void
  clearPicked: () => void
  onDateChange?: (day: string) => void
}

type VisitHandlers = {
  onDateChange: (date: Dayjs) => void
  onTimeChange: (time: Dayjs) => void
  onDurationChange: (duration: number) => void
  onRoomChange: (room: string) => void
}

// The VisitRow edits: each re-runs the guards and drops any picked slot.
export const useVisitHandlers = ({
  values,
  timeZone,
  rooms,
  setFieldValue,
  setCriteria,
  refreshGuards,
  clearPicked,
  onDateChange,
}: Args): VisitHandlers => ({
  onDateChange: (date) => {
    const time = dayjs(values.time)
      .year(date.year())
      .month(date.month())
      .date(date.date())
      .toDate()
    const day = date.format('YYYY-MM-DD')
    const room = roomForDay(String(values.room ?? ''), day, timeZone, rooms)
    const patch = room !== String(values.room ?? '') ? { time, room } : { time }
    setFieldValue(NEW_PATIENT_FORM_KEYS.DATE, day)
    setFieldValue(NEW_PATIENT_FORM_KEYS.TIME, time)
    if (patch.room) setFieldValue(NEW_PATIENT_FORM_KEYS.ROOM, patch.room)
    onDateChange?.(day)
    setCriteria((current) => ({ ...current, windowStart: day }))
    refreshGuards(patch)
    clearPicked()
  },
  onTimeChange: (time) => {
    setFieldValue(NEW_PATIENT_FORM_KEYS.TIME, time.toDate())
    clearPicked()
    refreshGuards({ time: time.toDate() })
  },
  onDurationChange: (duration) => {
    setFieldValue(NEW_PATIENT_FORM_KEYS.DURATION, duration)
    refreshGuards({ duration })
    clearPicked()
  },
  onRoomChange: (room) => {
    setFieldValue(NEW_PATIENT_FORM_KEYS.ROOM, room)
    refreshGuards({ room })
    clearPicked()
  },
})
